import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Hook that initializes the view mode from the user's profile preference.
 * 
 * The profile stores 'cards' or 'plantafel'.
 * For pages that use 'cards'/'list', 'plantafel' maps to 'list'.
 * For pages that use 'cards'/'plantafel'/'calendar', it maps directly.
 * 
 * @param defaultMode - fallback when no preference is set (default: 'cards')
 * @param modeMapping - optional mapping from profile value to page-specific value
 */
export function usePreferredViewMode<T extends string>(
  defaultMode: T,
  modeMapping?: Record<string, T>
): [T, React.Dispatch<React.SetStateAction<T>>, boolean] {
  const { user } = useAuth();
  const [viewMode, setViewMode] = useState<T>(defaultMode);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized && user?.preferredViewMode) {
      let mapped: T;
      if (modeMapping && user.preferredViewMode in modeMapping) {
        mapped = modeMapping[user.preferredViewMode];
      } else {
        mapped = user.preferredViewMode as T;
      }
      setViewMode(mapped);
      setInitialized(true);
    }
  }, [user?.preferredViewMode, initialized, modeMapping]);

  return [viewMode, setViewMode, initialized];
}

/**
 * Convenience: standard mapping for pages with 'cards' | 'list' toggle.
 * Profile 'plantafel' → page 'list', Profile 'cards' → page 'cards'.
 */
export const STANDARD_VIEW_MODE_MAP: Record<string, 'cards' | 'list'> = {
  cards: 'cards',
  plantafel: 'list',
};

